import React, { useRef, useState } from "react";
import { Typography, Box, Button, Link } from "@mui/material";
import {
  VariableSizeList as WindowList,
  ListChildComponentProps,
} from "react-window";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch } from "../../../../redux/store";
import {
  fetchCortexState,
  translateTexts,
} from "../../../../redux/actions/conversationsActions";
import { setAllMessagesTranslation } from "../../../../redux/reducers/conversationsReducer";
import { getTargetId } from "../../../../utils/render";
import MessageBubble from "../MessageBubble";
import SessionAudio from "../audio/SessionAudio";
import CortexModal from "./CortexModal";
import { Message } from "../../../../redux/reducers/conversationsReducer";

interface ItemData {
  listItems: any[];
  getCommentsForMessage: (targetId: string) => any[];
  conversationId: string | undefined;
  organizationId?: string;
  showRealData: boolean;
  canReadConversation?: boolean;
  itemHeights: React.MutableRefObject<Record<number, number>>;
  listRef: React.RefObject<any>;
  highlightedItemId: string | null;
  highlightedCommentId: string | null;
  searchTerm: string;
  adminUser: any;
  setPreventTargetScroll: (val: boolean) => void;
  getContextMessages: (messageId: number, chatSessionId: string) => Message[];
  chatSessionMap: Record<string, any>; // ← we pass in the sessions map
  cortexModalOpen: boolean;
  cortexModalSessionId: string | null;
  openCortexModal: (sessionId: string) => void;
  closeCortexModal: () => void;
}

interface MessageListProps {
  listItems: any[];
  getCommentsForMessage: (targetId: string) => any[];
  conversationId: string | undefined;
  showRealData: boolean;
  canReadConversation?: boolean;
  itemHeights: React.MutableRefObject<Record<number, number>>;
  listRef: React.RefObject<any>;
  highlightedItemId: string | null;
  highlightedCommentId: string | null;
  searchTerm: string;
  adminUser: any;
  setPreventTargetScroll: (val: boolean) => void;
  handleScroll: (props: any) => void;
  getItemSize: (index: number) => number;
  chatSessionMap: Record<string, any>;
}

// Helper to get message items around a given messageId for logs, etc.
function createGetContextMessages(
  listItems: any[]
): (messageId: number, chatSessionId: string) => Message[] {
  return (messageId: number, chatSessionId: string) => {
    const idx = listItems.findIndex(
      (item) =>
        item.type === "message" &&
        item?.message?.chat_session_id === chatSessionId &&
        (item?.message?.id === messageId ||
          item?.message?.chat_message_id === messageId)
    );
    if (idx === -1) return [];

    const start = Math.max(idx - 7, 0);
    const end = Math.min(idx + 7, listItems.length - 1);

    const contextMessages: Message[] = [];
    for (let i = start; i <= end; i++) {
      const item = listItems[i];
      if (item.type === "message") {
        contextMessages.push(item.message);
      }
    }
    return contextMessages;
  };
}

const MessageList: React.FC<MessageListProps> = ({
  listItems,
  getCommentsForMessage,
  conversationId,
  showRealData,
  canReadConversation,
  itemHeights,
  listRef,
  highlightedItemId,
  highlightedCommentId,
  searchTerm,
  adminUser,
  setPreventTargetScroll,
  handleScroll,
  getItemSize,
  chatSessionMap,
}) => {
  // Redux dispatch and navigation
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();

  // State for cortex modal
  const [cortexModalOpen, setCortexModalOpen] = useState(false);
  const [cortexModalSessionId, setCortexModalSessionId] = useState<
    string | null
  >(null);

  // Modal handlers
  const openCortexModal = async (sessionId: string) => {
    if (!conversationId) return;

    setCortexModalSessionId(sessionId);
    setCortexModalOpen(true);

    // Dispatch fetchCortexState to get the cortex data
    await dispatch(
      fetchCortexState({
        chatSessionId: sessionId,
        conversationId: conversationId,
      })
    );
  };

  const closeCortexModal = () => {
    setCortexModalOpen(false);
    setCortexModalSessionId(null);
  };

  // Build the context function once
  const getContextMessages = createGetContextMessages(listItems);
  const getSpeakerName = (msg: Message) => {
    const sessionId = msg.chat_session_id;
    const speakerId = msg.chat_speaker_id;
    // fallback if missing
    if (!sessionId || !speakerId) return "Unknown Speaker";

    // do a lookup from your map
    const session = chatSessionMap[sessionId];
    if (!session) return "Unknown Speaker";

    const speakerObj = session.speakers?.[speakerId];
    return speakerObj?.addressable_name
      ? speakerObj?.addressable_name
      : msg.type === "human"
      ? "Human"
      : "Bot";
  };

  const ListItemRenderer: React.FC<ListChildComponentProps<ItemData>> = ({
    index,
    style,
    data,
  }) => {
    const {
      listItems,
      getCommentsForMessage,
      conversationId,
      showRealData,
      canReadConversation,
      itemHeights,
      listRef,
      highlightedItemId,
      highlightedCommentId,
      searchTerm,
      adminUser,
      setPreventTargetScroll,
      getContextMessages,
      openCortexModal,
    } = data;

    const item = listItems[index];
    const itemRef = useRef<HTMLDivElement>(null);

    if (item.type === "date") {
      const isHighlighted = highlightedItemId === item.date;
      return (
        <div
          style={{
            ...style,
            overflow: "visible",
            backgroundColor: isHighlighted ? "#ffffcc" : "inherit",
          }}
          ref={itemRef}
          id={item.date}
        >
          <Box sx={{ mb: 4 }}>
            <Typography variant="h6" gutterBottom textAlign="center">
              {item.date}
            </Typography>
          </Box>
        </div>
      );
    } else if (item.type === "session") {
      // Possibly show a "New Session" marker with optional audio
      const canViewMessageAudio =
        adminUser?.scopes?.includes("storedaudio:read");

      // Get the session data from the chatSessionMap
      const sessionData = chatSessionMap[item.sessionId];
      const agentConfigId = sessionData?.agent_config?.id;
      const launchArgsId = sessionData?.launch_args_id;
      const behaviorConfig = sessionData?.behavior_config;

      // Extract behavior config details
      const behaviorType = behaviorConfig?.behavior_type;
      const assessmentKey = behaviorConfig?.assessment?.assessment_key;

      // Get assessment_id from metadata first, then fall back to behavior_config
      // TODO: Remove fallback eventually once confident all sessions have metadata populated
      const assessmentId =
        sessionData?.metadata?.assessment?.assessment_id ||
        behaviorConfig?.assessment?.interview_resource_id;

      // Get roleplay_id from metadata first, then fall back to behavior_config
      // TODO: Remove fallback eventually once confident all sessions have metadata populated
      const roleplayId =
        sessionData?.metadata?.roleplay?.roleplay_id ||
        behaviorConfig?.roleplay?.roleplay_resource_id;

      const roleplayScenarioKey =
        behaviorConfig?.roleplay_demo?.roleplay_scenario_key;
      // Get organization_id directly from the chat session
      const sessionOrganizationId =
        sessionData?.organization_id ||
        sessionData?.agent_config?.organization_id;

      return (
        <div style={{ ...style, overflow: "visible" }} ref={itemRef}>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "space-between",
              my: 2,
            }}
          >
            <Typography variant="subtitle1" sx={{ color: "gray" }}>
              New Session: {item.sessionId}
            </Typography>

            {/* Agent Config and Launch Args */}
            {(agentConfigId || launchArgsId) && (
              <Typography variant="caption" sx={{ color: "gray", mt: 0.5 }}>
                {agentConfigId && (
                  <>
                    Agent Config:{" "}
                    <Link
                      component="button"
                      variant="caption"
                      onClick={() =>
                        navigate(`/dashboard/agent-configs/${agentConfigId}`)
                      }
                      sx={{ cursor: "pointer" }}
                    >
                      {agentConfigId}
                    </Link>
                  </>
                )}
                {agentConfigId && launchArgsId && " | "}
                {launchArgsId && `Launch Args: ${launchArgsId}`}
              </Typography>
            )}

            {/* Behavior Config Details */}
            {behaviorType && (
              <Typography variant="caption" sx={{ color: "gray", mt: 0.5 }}>
                Behavior: {behaviorType}
                {behaviorType === "assessment" && assessmentId && (
                  <>
                    {" - "}
                    <Link
                      component="button"
                      variant="caption"
                      onClick={() => {
                        const id = assessmentId;
                        if (id && sessionOrganizationId) {
                          navigate(
                            `/dashboard/assessments/${sessionOrganizationId}/${id}`
                          );
                        }
                      }}
                      sx={{ cursor: "pointer" }}
                    >
                      {assessmentId}
                    </Link>
                  </>
                )}
                {behaviorType === "assessment" && (
                  <> assessment_key: {assessmentKey}</>
                )}
                {behaviorType === "roleplay_demo" && roleplayId && (
                  <>
                    {" - "}
                    <Link
                      component="button"
                      variant="caption"
                      onClick={() => {
                        if (roleplayId && sessionOrganizationId) {
                          navigate(
                            `/dashboard/roleplays/${sessionOrganizationId}/${roleplayId}`
                          );
                        }
                      }}
                      sx={{ cursor: "pointer" }}
                    >
                      {roleplayId}
                    </Link>
                  </>
                )}
                {behaviorType === "roleplay_demo" && roleplayScenarioKey && (
                  <> | roleplay_key: {roleplayScenarioKey}</>
                )}
              </Typography>
            )}

            <Box sx={{ display: "flex", gap: 1, alignItems: "center", mt: 1 }}>
              {canViewMessageAudio ? (
                <SessionAudio
                  conversationId={conversationId ?? ""}
                  chatSessionId={item.sessionId}
                />
              ) : null}
              <Button
                variant="outlined"
                size="small"
                onClick={() => openCortexModal(item.sessionId)}
              >
                View Cortex State
              </Button>
              {/* View Assessed Session button - show for assessment behavior type */}
              {behaviorType === "assessment" &&
                assessmentId &&
                sessionOrganizationId && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      // Navigate to assessment view with chat session filter
                      navigate(
                        `/dashboard/assessments/${sessionOrganizationId}/${assessmentId}?chat_session_id=${item.sessionId}#assessed-sessions`
                      );
                    }}
                  >
                    View Assessed Session
                  </Button>
                )}
              {/* View Assessed Session button - show for roleplay behavior type */}
              {behaviorType === "roleplay_demo" &&
                roleplayId &&
                sessionOrganizationId && (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      // Navigate to roleplay view with chat session filter
                      navigate(
                        `/dashboard/roleplays/${sessionOrganizationId}/${roleplayId}?chat_session_id=${item.sessionId}#roleplay-sessions`
                      );
                    }}
                  >
                    View Roleplay Assessment
                  </Button>
                )}
              {/* Translate Session button - only show if session needs translation */}
              {(() => {
                const session = chatSessionMap[item.sessionId];
                const lang =
                  session?.metadata?.primary_language ||
                  session?.primary_language;
                const needsTranslation =
                  lang && lang !== "default" && lang !== "english";

                if (!needsTranslation) return null;

                const handleTranslateSession = async () => {
                  const sessionMessages = session?.messages || [];
                  const textsToTranslate: string[] = [];
                  const messageIds: string[] = [];

                  sessionMessages.forEach((msg: any) => {
                    if (msg.content && msg.content.trim()) {
                      textsToTranslate.push(msg.content);
                      const msgId = msg.id ?? msg.chat_message_id;
                      messageIds.push(getTargetId(msgId, item.sessionId));
                    }
                  });

                  if (textsToTranslate.length > 0) {
                    try {
                      await dispatch(
                        translateTexts({
                          texts: textsToTranslate,
                          targetLanguage: "en",
                          sourceLanguage: lang,
                        })
                      ).unwrap();
                      dispatch(
                        setAllMessagesTranslation({ messageIds, show: true })
                      );
                    } catch (error) {
                      console.error("Failed to translate session:", error);
                    }
                  }
                };

                return (
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={handleTranslateSession}
                  >
                    Translate Session
                  </Button>
                );
              })()}
            </Box>
          </Box>
        </div>
      );
    } else if (item.type === "message") {
      const { message, targetId } = item;
      const messageComments = getCommentsForMessage(targetId);

      const speakerName = getSpeakerName(message);

      // Get session language from chatSessionMap
      const sessionId = message.chat_session_id;
      const session = chatSessionMap[sessionId];
      // For real sessions, get from metadata.primary_language
      // For anonymized sessions, get from primary_language directly
      const sessionLanguage =
        session?.metadata?.primary_language ||
        session?.primary_language ||
        "default";

      return (
        <div
          style={{
            ...style,
            overflow: "visible",
            width: "98%",
          }}
          ref={itemRef}
          id={targetId}
        >
          <MessageBubble
            message={message}
            conversationId={conversationId}
            isRightAligned={message.type !== "human"}
            canViewAllData={showRealData && canReadConversation}
            canReadConversation={canReadConversation}
            comments={messageComments}
            chatSessionId={message?.chat_session_id}
            onHeightChange={(height) => {
              if (itemHeights.current[index] !== height) {
                itemHeights.current[index] = height;
                if (listRef.current) {
                  listRef.current.resetAfterIndex(index);
                }
              }
            }}
            onCommentAdded={() => setPreventTargetScroll(true)}
            isHighlighted={highlightedItemId === targetId}
            highlightedCommentId={highlightedCommentId}
            searchTerm={searchTerm}
            getContextMessages={getContextMessages}
            speakerName={speakerName}
            getSpeakerName={getSpeakerName}
            sessionLanguage={sessionLanguage}
          />
        </div>
      );
    } else if (item.type === "spacer") {
      return (
        <div
          style={{
            ...style,
          }}
          ref={itemRef}
        />
      );
    }
    return null;
  };

  return (
    <>
      <WindowList
        ref={listRef}
        height={window.innerHeight}
        itemCount={listItems.length}
        itemSize={getItemSize}
        width="100%"
        onScroll={handleScroll}
        overscanCount={10}
        itemData={{
          listItems,
          getCommentsForMessage,
          conversationId,
          showRealData,
          canReadConversation,
          itemHeights,
          listRef,
          highlightedItemId,
          highlightedCommentId,
          searchTerm,
          adminUser,
          setPreventTargetScroll,
          getContextMessages,
          chatSessionMap,
          cortexModalOpen,
          cortexModalSessionId,
          openCortexModal,
          closeCortexModal,
        }}
      >
        {ListItemRenderer}
      </WindowList>

      <CortexModal
        open={cortexModalOpen}
        onClose={closeCortexModal}
        conversationId={conversationId}
        chatSessionId={cortexModalSessionId || undefined}
      />
    </>
  );
};

export default MessageList;
